import { useEffect, useRef } from 'react';

/**
 * Custom hook that gently pulls an interactive element toward the cursor on hover.
 *
 * @param {number} strength - Pull strength factor (default: 0.25)
 * @returns {React.RefObject} - Ref to attach to the target element
 */
export default function useMagneticEffect(strength = 0.25) {
  const elementRef = useRef(null);

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    if (
      typeof window !== 'undefined' &&
      (window.matchMedia('(hover: none)').matches ||
       window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    ) {
      return;
    }

    let animationFrameId = null;

    const handleMouseMove = (e) => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);

      animationFrameId = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;

        const deltaX = (e.clientX - centerX) * strength;
        const deltaY = (e.clientY - centerY) * strength;

        el.style.transform = `translate3d(${deltaX.toFixed(2)}px, ${deltaY.toFixed(2)}px, 0)`;
        el.style.transition = 'transform 0.1s ease-out';
      });
    };

    const handleMouseLeave = () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);

      animationFrameId = requestAnimationFrame(() => {
        el.style.transform = 'translate3d(0, 0, 0)';
        el.style.transition = 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)';
      });
    };

    el.addEventListener('mousemove', handleMouseMove, { passive: true });
    el.addEventListener('mouseleave', handleMouseLeave, { passive: true });

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [strength]);

  return elementRef;
}
